// FieldDesk — экран Отчёты.
//
// Архитектура:
//   - Один источник правды: GET /api/reports/visits возвращает KPI + полный
//     список визитов с computed-полями (wasLate, addressDeviation, ...).
//   - Excel-экспорт генерируется на клиенте (xlsx/sheetjs) — backend не
//     грузим, данные уже на руках.
//   - Фильтры применяются debounce 300мс — лишние GET'ы не плодим.
//
// Фильтры:
//   - period: пресет (Сегодня/Вчера/Эта неделя/...) ИЛИ произвольный диапазон;
//   - engineer: «Все» либо конкретный;
//   - types: мульти-выбор VisitType.
//
// Таблица режется на клиенте по 50 строк; export тянет ВСЕ строки.

import { useEffect, useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import * as XLSX from 'xlsx';
import { useAuth } from '../auth/AuthContext';
import type {
  DashboardResponse,
  EngineerListItem,
  ReportVisitsKpi,
  ReportVisitsResponse,
  VisitDto,
  VisitType,
} from '../api/types';
import { STATUS_SCHEMA, TYPE_COLOR, TYPE_LABEL } from '../styles/theme';
import { ErrorStrip } from './Dashboard';

// --- helpers: пресеты периодов ---------------------------------------------
type PeriodPreset =
  | 'today'
  | 'yesterday'
  | 'thisWeek'
  | 'lastWeek'
  | 'thisMonth'
  | 'lastMonth'
  | 'custom';

const PRESET_LABEL: Record<PeriodPreset, string> = {
  today: 'Сегодня',
  yesterday: 'Вчера',
  thisWeek: 'Эта неделя',
  lastWeek: 'Прошлая неделя',
  thisMonth: 'Этот месяц',
  lastMonth: 'Прошлый месяц',
  custom: 'Произвольный',
};

function ymd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function presetToRange(p: PeriodPreset): { from: string; to: string } {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (p === 'today') return { from: ymd(today), to: ymd(today) };
  if (p === 'yesterday') {
    const y = new Date(today);
    y.setDate(y.getDate() - 1);
    return { from: ymd(y), to: ymd(y) };
  }
  if (p === 'thisWeek') {
    const monday = new Date(today);
    monday.setDate(today.getDate() - ((today.getDay() + 6) % 7));
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return { from: ymd(monday), to: ymd(sunday) };
  }
  if (p === 'lastWeek') {
    const thisMon = new Date(today);
    thisMon.setDate(today.getDate() - ((today.getDay() + 6) % 7));
    const lastMon = new Date(thisMon);
    lastMon.setDate(thisMon.getDate() - 7);
    const lastSun = new Date(lastMon);
    lastSun.setDate(lastMon.getDate() + 6);
    return { from: ymd(lastMon), to: ymd(lastSun) };
  }
  if (p === 'thisMonth') {
    const first = new Date(today.getFullYear(), today.getMonth(), 1);
    const last = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    return { from: ymd(first), to: ymd(last) };
  }
  if (p === 'lastMonth') {
    const first = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const last = new Date(today.getFullYear(), today.getMonth(), 0);
    return { from: ymd(first), to: ymd(last) };
  }
  // custom — заглушка, диапазон диктует UI.
  return { from: ymd(today), to: ymd(today) };
}

const ALL_TYPES: VisitType[] = ['INSTALLATION', 'TRAINING', 'PAID_VISIT'];

// --- main screen -----------------------------------------------------------

type ReportsTab = 'dashboard' | 'detail';

interface Props {
  onOpenVisit?(v: VisitDto): void;
}

export function ReportsScreen({ onOpenVisit }: Props) {
  const [activeTab, setActiveTab] = useState<ReportsTab>('dashboard');

  return (
    <div style={{ padding: 24, height: '100%', overflow: 'auto' }}>
      <h1 style={{ fontSize: 20, fontWeight: 700, marginBottom: 14 }}>Отчёты</h1>

      {/* Tab switcher */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 16 }}>
        {([
          { id: 'dashboard', label: '📊 Дашборд' },
          { id: 'detail',    label: '📋 Детальный отчёт' },
        ] as { id: ReportsTab; label: string }[]).map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '8px 16px',
              fontSize: 13,
              fontWeight: 600,
              border: 'none',
              borderRadius: 8,
              cursor: 'pointer',
              background: activeTab === tab.id ? '#2563eb' : '#e2e8f0',
              color: activeTab === tab.id ? '#fff' : '#475569',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'dashboard' ? (
        <DashboardView />
      ) : (
        <DetailView onOpenVisit={onOpenVisit} />
      )}
    </div>
  );
}

// --- Dashboard view --------------------------------------------------------

function DashboardView() {
  const { api } = useAuth();
  const [period, setPeriod] = useState<'week' | 'month'>('week');
  const [data, setData] = useState<DashboardResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setErr(null);
    api.getReportDashboard(period)
      .then(setData)
      .catch((e: Error) => setErr(e.message))
      .finally(() => setLoading(false));
  }, [api, period]);

  return (
    <>
      {/* Period switcher */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
        <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>Период:</span>
        {([
          { v: 'week',  label: 'Эта неделя' },
          { v: 'month', label: 'Этот месяц' },
        ] as { v: 'week' | 'month'; label: string }[]).map((opt) => (
          <button
            key={opt.v}
            onClick={() => setPeriod(opt.v)}
            style={{
              padding: '5px 12px',
              fontSize: 12,
              fontWeight: 600,
              border: `1px solid ${period === opt.v ? '#2563eb' : '#e2e8f0'}`,
              borderRadius: 6,
              cursor: 'pointer',
              background: period === opt.v ? '#2563eb' : '#fff',
              color: period === opt.v ? '#fff' : '#475569',
            }}
          >
            {opt.label}
          </button>
        ))}
        {data && (
          <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 8 }}>
            {data.from} – {data.to}
          </span>
        )}
      </div>

      {err && <div style={{ marginBottom: 14 }}><ErrorStrip message={err} /></div>}

      {loading && !data ? (
        <div style={{ marginTop: 30, color: '#94a3b8', textAlign: 'center' }}>Загружаем дашборд…</div>
      ) : data ? (
        <>
          <DashKpiRow data={data} />
          <DashChartsRow data={data} />
          <EngineerWorkloadChart data={data} />
        </>
      ) : null}
    </>
  );
}

// --- Dashboard KPI cards ---------------------------------------------------

function DashKpiRow({ data }: { data: DashboardResponse }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 14 }}>
      <DashKpiCard label="Всего выездов"    value={data.totalVisits}     color="#2563eb" />
      <DashKpiCard label="Выполнено"        value={`${data.completedVisits} (${data.completionRate}%)`} color="#16a34a" />
      <DashKpiCard label="Отменено"         value={data.cancelledVisits}  color="#dc2626" />
      <DashKpiCard label="С опозданием"     value={`${data.lateVisits} (${data.lateRate}%)`} color="#d97706" />
    </div>
  );
}

function DashKpiCard({ label, value, color }: { label: string; value: string | number; color: string }) {
  return (
    <div style={{ background: '#fff', borderRadius: 12, padding: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.07)' }}>
      <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, color, lineHeight: 1.1 }}>{value}</div>
    </div>
  );
}

// --- Dashboard charts (pie + line) ----------------------------------------

function DashChartsRow({ data }: { data: DashboardResponse }) {
  const typeData = data.byType.map((t) => ({
    name: t.label,
    value: t.count,
    color: TYPE_COLOR[t.type] ?? '#64748b',
  }));

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 14, marginBottom: 14 }}>
      <ChartPanel title="По типам">
        <ResponsiveContainer width="100%" height={220}>
          <PieChart>
            <Pie data={typeData} dataKey="value" nameKey="name" outerRadius={80} label>
              {typeData.map((d, i) => <Cell key={i} fill={d.color} />)}
            </Pie>
            <Tooltip />
            <Legend wrapperStyle={{ fontSize: 11 }} />
          </PieChart>
        </ResponsiveContainer>
      </ChartPanel>

      <ChartPanel title="Динамика по дням">
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={data.byDay}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="date" stroke="#64748b" fontSize={10} />
            <YAxis stroke="#64748b" fontSize={10} allowDecimals={false} />
            <Tooltip />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Line type="monotone" dataKey="total"     name="Всего"     stroke="#2563eb" strokeWidth={2} dot={{ r: 2 }} />
            <Line type="monotone" dataKey="completed" name="Выполнено" stroke="#16a34a" strokeWidth={2} dot={{ r: 2 }} />
            <Line type="monotone" dataKey="late"      name="Опоздания" stroke="#dc2626" strokeWidth={2} dot={{ r: 2 }} />
          </LineChart>
        </ResponsiveContainer>
      </ChartPanel>
    </div>
  );
}

// --- Engineer workload bar chart ------------------------------------------
// Цвет бара: зелёный < warn, жёлтый warn..high, красный > high выездов.
// Пороги приходят с backend (настраиваются в «Настройках»); дефолт 5/8 —
// на случай старого backend без поля workloadThresholds.

const DEFAULT_WORKLOAD_THRESHOLDS = { warn: 5, high: 8 };

function workloadColor(total: number, t: { warn: number; high: number }): string {
  if (total > t.high) return '#dc2626';
  if (total >= t.warn) return '#d97706';
  return '#16a34a';
}

function EngineerWorkloadChart({ data }: { data: DashboardResponse }) {
  if (data.byEngineer.length === 0) {
    return (
      <ChartPanel title="Нагрузка инженеров">
        <div style={{ padding: 20, color: '#94a3b8', textAlign: 'center', fontSize: 13 }}>
          Нет данных за выбранный период.
        </div>
      </ChartPanel>
    );
  }

  const t = data.workloadThresholds ?? DEFAULT_WORKLOAD_THRESHOLDS;
  const chartData = data.byEngineer.map((e) => ({
    name: e.engineerName.split(' ')[0], // Имя (краткое для оси)
    fullName: e.engineerName,
    total: e.total,
    completed: e.completed,
    late: e.late,
    color: workloadColor(e.total, t),
  }));

  return (
    <ChartPanel title="Нагрузка инженеров">
      <div style={{ fontSize: 11, color: '#64748b', marginBottom: 8 }}>
        🟢 &lt;{t.warn} · 🟡 {t.warn}–{t.high} · 🔴 &gt;{t.high} выездов за {data.period === 'week' ? 'неделю' : 'месяц'}
        {' '}(отменённые не считаются)
      </div>
      <ResponsiveContainer width="100%" height={Math.max(200, chartData.length * 36)}>
        <BarChart data={chartData} layout="vertical" margin={{ left: 8, right: 24, top: 4, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
          <XAxis type="number" stroke="#64748b" fontSize={11} allowDecimals={false} />
          <YAxis type="category" dataKey="name" stroke="#64748b" fontSize={11} width={80} />
          <Tooltip
            content={({ active, payload }) => {
              if (!active || !payload?.[0]) return null;
              const d = payload[0].payload as typeof chartData[number];
              return (
                <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 12px', fontSize: 12 }}>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>{d.fullName}</div>
                  <div>Всего: <b>{d.total}</b></div>
                  <div style={{ color: '#16a34a' }}>Выполнено: {d.completed}</div>
                  <div style={{ color: '#dc2626' }}>Опоздания: {d.late}</div>
                </div>
              );
            }}
          />
          <Bar dataKey="total" name="Выездов" radius={[0, 4, 4, 0]}>
            {chartData.map((d, i) => <Cell key={i} fill={d.color} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartPanel>
  );
}

// --- Detail view (existing reports logic) ---------------------------------

function DetailView({ onOpenVisit }: { onOpenVisit?: (v: VisitDto) => void }) {
  const { api } = useAuth();
  const [preset, setPreset] = useState<PeriodPreset>('thisMonth');
  const initRange = useMemo(() => presetToRange('thisMonth'), []);
  const [from, setFrom] = useState<string>(initRange.from);
  const [to, setTo] = useState<string>(initRange.to);
  const [engineerId, setEngineerId] = useState<number | null>(null);
  const [types, setTypes] = useState<VisitType[]>(ALL_TYPES);

  const [engineers, setEngineers] = useState<EngineerListItem[]>([]);
  const [data, setData] = useState<ReportVisitsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api.listEngineers().then(setEngineers).catch((e: Error) => setErr(e.message));
  }, [api]);

  useEffect(() => {
    if (preset === 'custom') return;
    const r = presetToRange(preset);
    setFrom(r.from);
    setTo(r.to);
  }, [preset]);

  useEffect(() => {
    setLoading(true);
    const handle = setTimeout(() => {
      api
        .getReportVisits({
          from,
          to,
          engineerId: engineerId ?? undefined,
          types: types.length === ALL_TYPES.length ? undefined : types,
        })
        .then((d) => { setData(d); setErr(null); })
        .catch((e: Error) => setErr(e.message))
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(handle);
  }, [api, from, to, engineerId, types]);

  function handleExport() {
    if (!data) return;
    exportToExcel(data);
  }

  return (
    <>
      <FiltersBar
        preset={preset}
        from={from}
        to={to}
        engineerId={engineerId}
        engineers={engineers}
        types={types}
        onPresetChange={setPreset}
        onFromChange={(v) => { setPreset('custom'); setFrom(v); }}
        onToChange={(v) => { setPreset('custom'); setTo(v); }}
        onEngineerChange={setEngineerId}
        onTypesChange={setTypes}
        onExport={handleExport}
        canExport={!!data && data.visits.length > 0}
      />

      {err && <div style={{ marginTop: 14 }}><ErrorStrip message={err} /></div>}

      {loading && !data ? (
        <div style={{ marginTop: 30, color: '#94a3b8', textAlign: 'center' }}>Загружаем отчёт…</div>
      ) : data ? (
        <>
          <KpiRow data={data} />
          <ChartsRow data={data} />
          <VisitsTable data={data} onOpenVisit={onOpenVisit} />
        </>
      ) : null}
    </>
  );
}

// --- Filters bar -----------------------------------------------------------

interface FiltersProps {
  preset: PeriodPreset;
  from: string;
  to: string;
  engineerId: number | null;
  engineers: EngineerListItem[];
  types: VisitType[];
  onPresetChange(p: PeriodPreset): void;
  onFromChange(v: string): void;
  onToChange(v: string): void;
  onEngineerChange(id: number | null): void;
  onTypesChange(t: VisitType[]): void;
  onExport(): void;
  canExport: boolean;
}

function FiltersBar({
  preset,
  from,
  to,
  engineerId,
  engineers,
  types,
  onPresetChange,
  onFromChange,
  onToChange,
  onEngineerChange,
  onTypesChange,
  onExport,
  canExport,
}: FiltersProps) {
  function toggleType(t: VisitType) {
    onTypesChange(types.includes(t) ? types.filter((x) => x !== t) : [...types, t]);
  }
  return (
    <div
      style={{
        background: '#fff',
        borderRadius: 12,
        padding: 14,
        boxShadow: '0 1px 3px rgba(0,0,0,0.07)',
        display: 'flex',
        flexWrap: 'wrap',
        gap: 10,
        alignItems: 'flex-end',
      }}
    >
      <FieldGroup label="Период">
        <select
          value={preset}
          onChange={(e) => onPresetChange(e.target.value as PeriodPreset)}
          style={selectStyle}
        >
          {(Object.keys(PRESET_LABEL) as PeriodPreset[]).map((p) => (
            <option key={p} value={p}>
              {PRESET_LABEL[p]}
            </option>
          ))}
        </select>
      </FieldGroup>

      <FieldGroup label="С">
        <input type="date" value={from} onChange={(e) => onFromChange(e.target.value)} style={selectStyle} />
      </FieldGroup>
      <FieldGroup label="По">
        <input type="date" value={to} onChange={(e) => onToChange(e.target.value)} style={selectStyle} />
      </FieldGroup>

      <FieldGroup label="Инженер">
        <select
          value={engineerId ?? ''}
          onChange={(e) => onEngineerChange(e.target.value ? Number(e.target.value) : null)}
          style={selectStyle}
        >
          <option value="">Все инженеры</option>
          {engineers.map((e) => (
            <option key={e.id} value={e.id}>
              {e.fullName}
            </option>
          ))}
        </select>
      </FieldGroup>

      <FieldGroup label="Типы">
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {ALL_TYPES.map((t) => {
            const active = types.includes(t);
            const c = TYPE_COLOR[t] ?? '#64748b';
            return (
              <button
                key={t}
                onClick={() => toggleType(t)}
                style={{
                  padding: '5px 10px',
                  fontSize: 11,
                  border: `1px solid ${active ? c : '#e2e8f0'}`,
                  background: active ? c + '18' : '#fff',
                  color: active ? c : '#64748b',
                  fontWeight: 600,
                  borderRadius: 6,
                  cursor: 'pointer',
                }}
              >
                {TYPE_LABEL[t]}
              </button>
            );
          })}
        </div>
      </FieldGroup>

      <div style={{ marginLeft: 'auto' }}>
        <button
          onClick={onExport}
          disabled={!canExport}
          style={{
            padding: '8px 14px',
            background: canExport ? '#16a34a' : '#cbd5e1',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 600,
            cursor: canExport ? 'pointer' : 'not-allowed',
          }}
        >
          📥 Экспорт в Excel
        </button>
      </div>
    </div>
  );
}

const selectStyle: React.CSSProperties = {
  padding: '6px 10px',
  border: '1px solid #e2e8f0',
  borderRadius: 6,
  fontSize: 13,
  background: '#fff',
  color: '#0f172a',
  minWidth: 130,
};

function FieldGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <label style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>{label}</label>
      {children}
    </div>
  );
}

// --- KPI row ---------------------------------------------------------------

function KpiRow({ data }: { data: ReportVisitsResponse }) {
  const { kpi, previousKpi } = data;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginTop: 14 }}>
      <KpiCard label="Всего выездов" value={kpi.total} prev={previousKpi?.total} color="#2563eb" />
      <KpiCard label="Выполнено" value={kpi.completed} prev={previousKpi?.completed} color="#16a34a" />
      <KpiCard label="Отменено" value={kpi.cancelled} prev={previousKpi?.cancelled} color="#dc2626" inverse />
      <KpiCard label="С опозданием" value={kpi.late} prev={previousKpi?.late} color="#d97706" inverse />
      <KpiCard
        label="Среднее опоздание"
        value={kpi.avgLateMinutes != null ? `${kpi.avgLateMinutes} мин` : '—'}
        prevRaw={previousKpi?.avgLateMinutes}
        currentRaw={kpi.avgLateMinutes}
        color="#7c3aed"
        inverse
      />
    </div>
  );
}

interface KpiCardProps {
  label: string;
  value: number | string;
  prev?: number | null;
  /** Для текстовых значений (среднее опоздание): сравниваем сырыми числами. */
  prevRaw?: number | null;
  currentRaw?: number | null;
  color: string;
  /** Инверсия знака тренда: рост отмен/опозданий — это плохо. */
  inverse?: boolean;
}

function KpiCard({ label, value, prev, prevRaw, currentRaw, color, inverse }: KpiCardProps) {
  // Унифицированно решаем какая дельта показывается.
  const cur = typeof value === 'number' ? value : currentRaw ?? null;
  const previous = prev ?? prevRaw ?? null;

  let trend: { sign: '+' | '-' | '='; pct: number } | null = null;
  if (previous != null && cur != null && previous > 0) {
    const diff = cur - previous;
    const pct = Math.round((diff / previous) * 100);
    trend = { sign: diff > 0 ? '+' : diff < 0 ? '-' : '=', pct: Math.abs(pct) };
  }

  // «Хорошо» = зелёный; «плохо» = красный. inverse меняет интерпретацию.
  let trendColor = '#94a3b8';
  if (trend && trend.sign !== '=') {
    const isGood = inverse ? trend.sign === '-' : trend.sign === '+';
    trendColor = isGood ? '#16a34a' : '#dc2626';
  }

  return (
    <div style={{ background: '#fff', borderRadius: 12, padding: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.07)' }}>
      <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, color, lineHeight: 1.1 }}>{value}</div>
      {trend && (
        <div style={{ fontSize: 11, color: trendColor, marginTop: 4, fontWeight: 600 }}>
          {trend.sign === '=' ? '= 0%' : `${trend.sign === '+' ? '↑' : '↓'} ${trend.pct}%`} к предыдущему периоду
        </div>
      )}
    </div>
  );
}

// --- Charts ---------------------------------------------------------------

function ChartsRow({ data }: { data: ReportVisitsResponse }) {
  const typeData = (Object.entries(data.byType) as Array<[VisitType, number]>)
    .filter(([, n]) => n > 0)
    .map(([t, n]) => ({ name: TYPE_LABEL[t] ?? t, value: n, color: TYPE_COLOR[t] ?? '#64748b' }));

  const statusData = Object.entries(data.byStatus)
    .filter(([, n]) => n > 0)
    .map(([s, n]) => ({
      name: STATUS_SCHEMA[s]?.label ?? s,
      value: n,
      color: STATUS_SCHEMA[s]?.color ?? '#64748b',
    }));

  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginTop: 14 }}>
        <ChartPanel title="По типам">
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={typeData} dataKey="value" nameKey="name" outerRadius={90} label>
                {typeData.map((d, i) => (
                  <Cell key={i} fill={d.color} />
                ))}
              </Pie>
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartPanel>
        <ChartPanel title="По статусам">
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={statusData} dataKey="value" nameKey="name" outerRadius={90} label>
                {statusData.map((d, i) => (
                  <Cell key={i} fill={d.color} />
                ))}
              </Pie>
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartPanel>
      </div>

      <ChartPanel title="Динамика по дням" style={{ marginTop: 14 }}>
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={data.dailySeries}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="date" stroke="#64748b" fontSize={11} />
            <YAxis stroke="#64748b" fontSize={11} allowDecimals={false} />
            <Tooltip />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Line type="monotone" dataKey="total" name="Всего" stroke="#2563eb" strokeWidth={2} dot={{ r: 3 }} />
            <Line type="monotone" dataKey="late" name="С опозданием" stroke="#dc2626" strokeWidth={2} dot={{ r: 3 }} />
          </LineChart>
        </ResponsiveContainer>
      </ChartPanel>
    </>
  );
}

function ChartPanel({
  title,
  children,
  style,
}: {
  title: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <div
      style={{
        background: '#fff',
        borderRadius: 12,
        padding: 18,
        boxShadow: '0 1px 3px rgba(0,0,0,0.07)',
        ...(style ?? {}),
      }}
    >
      <h2 style={{ fontSize: 13, fontWeight: 600, marginBottom: 10, color: '#475569' }}>{title}</h2>
      {children}
    </div>
  );
}

// --- Visits table ----------------------------------------------------------

type SortKey = 'scheduledDate' | 'engineer' | 'client' | 'type' | 'status' | 'lateMinutes' | 'deviationDistanceMeters';
type SortDir = 'asc' | 'desc';

function VisitsTable({
  data,
  onOpenVisit,
}: {
  data: ReportVisitsResponse;
  onOpenVisit?: (v: VisitDto) => void;
}) {
  const PAGE_SIZE = 50;
  const [page, setPage] = useState(0);
  const [sort, setSort] = useState<{ key: SortKey; dir: SortDir }>({ key: 'scheduledDate', dir: 'asc' });

  useEffect(() => {
    setPage(0);
  }, [data]);

  const rows = useMemo(() => {
    const arr = [...data.visits];
    arr.sort((a, b) => {
      const dir = sort.dir === 'asc' ? 1 : -1;
      switch (sort.key) {
        case 'scheduledDate':
          return ((a.scheduledDate + a.scheduledStart) > (b.scheduledDate + b.scheduledStart) ? 1 : -1) * dir;
        case 'engineer':
          return a.engineer.fullName.localeCompare(b.engineer.fullName) * dir;
        case 'client':
          return ((a.client?.name ?? '').localeCompare(b.client?.name ?? '')) * dir;
        case 'type':
          return a.type.localeCompare(b.type) * dir;
        case 'status':
          return a.status.localeCompare(b.status) * dir;
        case 'lateMinutes':
          return ((a.lateMinutes ?? -1) - (b.lateMinutes ?? -1)) * dir;
        case 'deviationDistanceMeters':
          return ((a.deviationDistanceMeters ?? -1) - (b.deviationDistanceMeters ?? -1)) * dir;
        default:
          return 0;
      }
    });
    return arr;
  }, [data, sort]);

  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const pageRows = rows.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  function toggleSort(key: SortKey) {
    setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));
  }

  return (
    <div style={{ background: '#fff', borderRadius: 12, marginTop: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.07)', overflow: 'hidden' }}>
      <div style={{ padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ fontSize: 14, fontWeight: 600 }}>
          Список выездов{' '}
          <span style={{ color: '#94a3b8', fontWeight: 400 }}>
            · {rows.length} шт.
          </span>
        </h2>
        {totalPages > 1 && (
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12, color: '#64748b' }}>
            <button
              onClick={() => setPage(Math.max(0, page - 1))}
              disabled={page === 0}
              style={pagerBtn(page === 0)}
            >
              ←
            </button>
            <span>
              {page + 1} / {totalPages}
            </span>
            <button
              onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
              disabled={page >= totalPages - 1}
              style={pagerBtn(page >= totalPages - 1)}
            >
              →
            </button>
          </div>
        )}
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead style={{ background: '#f8fafc', borderTop: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0' }}>
            <tr>
              <Th onClick={() => toggleSort('scheduledDate')} active={sort.key === 'scheduledDate'} dir={sort.dir}>
                Дата
              </Th>
              <Th>Время план</Th>
              <Th>Время факт</Th>
              <Th onClick={() => toggleSort('lateMinutes')} active={sort.key === 'lateMinutes'} dir={sort.dir}>
                Опоздание
              </Th>
              <Th onClick={() => toggleSort('deviationDistanceMeters')} active={sort.key === 'deviationDistanceMeters'} dir={sort.dir}>
                Отклонение
              </Th>
              <Th onClick={() => toggleSort('type')} active={sort.key === 'type'} dir={sort.dir}>
                Тип
              </Th>
              <Th onClick={() => toggleSort('status')} active={sort.key === 'status'} dir={sort.dir}>
                Статус
              </Th>
              <Th onClick={() => toggleSort('engineer')} active={sort.key === 'engineer'} dir={sort.dir}>
                Инженер
              </Th>
              <Th onClick={() => toggleSort('client')} active={sort.key === 'client'} dir={sort.dir}>
                Клиент
              </Th>
              <Th>Адрес</Th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map((r) => (
              <tr
                key={r.id}
                onClick={() => onOpenVisit?.(r as unknown as VisitDto)}
                style={{ borderBottom: '1px solid #f1f5f9', cursor: onOpenVisit ? 'pointer' : 'default' }}
              >
                <td style={tdStyle}>{r.scheduledDate}</td>
                <td style={tdStyle}>{r.scheduledStart}–{r.scheduledEnd}</td>
                <td style={tdStyle}>{r.arrivedAt ? formatTime(r.arrivedAt) : '—'}</td>
                <td
                  style={{
                    ...tdStyle,
                    background: r.wasLate ? '#fef2f2' : undefined,
                    color: r.wasLate ? '#b91c1c' : undefined,
                    fontWeight: r.wasLate ? 600 : 400,
                  }}
                >
                  {r.lateMinutes != null ? `${r.lateMinutes} мин` : '—'}
                </td>
                <td
                  style={{
                    ...tdStyle,
                    background: r.addressDeviation ? '#fef3c7' : undefined,
                    color: r.addressDeviation ? '#92400e' : undefined,
                    fontWeight: r.addressDeviation ? 600 : 400,
                  }}
                >
                  {r.deviationDistanceMeters != null ? `${r.deviationDistanceMeters} м` : '—'}
                </td>
                <td style={tdStyle}>
                  <span
                    style={{
                      padding: '2px 6px',
                      fontSize: 10,
                      borderRadius: 4,
                      background: (TYPE_COLOR[r.type] ?? '#64748b') + '18',
                      color: TYPE_COLOR[r.type] ?? '#64748b',
                      fontWeight: 600,
                    }}
                  >
                    {TYPE_LABEL[r.type] ?? r.type}
                  </span>
                </td>
                <td style={tdStyle}>
                  <span
                    style={{
                      padding: '2px 6px',
                      fontSize: 10,
                      borderRadius: 4,
                      background: STATUS_SCHEMA[r.status]?.bg ?? '#f1f5f9',
                      color: STATUS_SCHEMA[r.status]?.color ?? '#64748b',
                      fontWeight: 600,
                    }}
                  >
                    {STATUS_SCHEMA[r.status]?.label ?? r.status}
                  </span>
                </td>
                <td style={tdStyle}>{r.engineer.fullName}</td>
                <td style={tdStyle}>{r.client?.name ?? '—'}</td>
                <td style={{ ...tdStyle, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {r.client?.address || '—'}
                </td>
              </tr>
            ))}
            {pageRows.length === 0 && (
              <tr>
                <td colSpan={10} style={{ padding: 30, textAlign: 'center', color: '#94a3b8' }}>
                  Нет данных за выбранный период.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function pagerBtn(disabled: boolean): React.CSSProperties {
  return {
    padding: '4px 8px',
    border: '1px solid #e2e8f0',
    borderRadius: 4,
    background: '#fff',
    color: disabled ? '#cbd5e1' : '#475569',
    cursor: disabled ? 'not-allowed' : 'pointer',
  };
}

const tdStyle: React.CSSProperties = { padding: '8px 12px', verticalAlign: 'middle' };

function Th({
  children,
  onClick,
  active,
  dir,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  active?: boolean;
  dir?: SortDir;
}) {
  return (
    <th
      onClick={onClick}
      style={{
        textAlign: 'left',
        padding: '8px 12px',
        fontSize: 11,
        fontWeight: 600,
        color: active ? '#0f172a' : '#64748b',
        cursor: onClick ? 'pointer' : 'default',
        userSelect: 'none',
        whiteSpace: 'nowrap',
      }}
    >
      {children} {active ? (dir === 'asc' ? '↑' : '↓') : ''}
    </th>
  );
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

// --- Excel export ----------------------------------------------------------

function exportToExcel(data: ReportVisitsResponse) {
  // Имя файла по правилу из спеки.
  const fname = `fielddesk-report-${data.filters.from}-${data.filters.to}.xlsx`;

  const rows = data.visits.map((r) => ({
    'Дата': r.scheduledDate,
    'Время план': `${r.scheduledStart}–${r.scheduledEnd}`,
    'Время факт': r.arrivedAt ? formatTime(r.arrivedAt) : '',
    'Опоздание (мин)': r.lateMinutes ?? '',
    'Отклонение (м)': r.deviationDistanceMeters ?? '',
    'Тип': TYPE_LABEL[r.type] ?? r.type,
    'Статус': STATUS_SCHEMA[r.status]?.label ?? r.status,
    'Инженер': r.engineer.fullName,
    'Клиент': r.client?.name ?? '',
    'Адрес': r.client?.address ?? '',
    'Координаты прибытия':
      r.arrivedLat != null && r.arrivedLng != null ? `${r.arrivedLat.toFixed(6)}, ${r.arrivedLng.toFixed(6)}` : '',
    'Координаты клиента':
      r.client?.latitude != null && r.client?.longitude != null
        ? `${r.client.latitude.toFixed(6)}, ${r.client.longitude.toFixed(6)}`
        : '',
    'Причина отмены': r.cancelReason ?? '',
  }));

  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Выезды');
  XLSX.writeFile(wb, fname);
}
